import { useCallback, useEffect, useRef, useState } from 'react';
import { PREFETCH_DEBOUNCE_MS } from '@/lib/searchPrefetch';

/**
 * A search form's pickers — travelers and dates — and how long its early
 * search (src/lib/searchPrefetch.js) should wait to start.
 *
 * While the travelers picker is open the form holds off: each + or − would
 * otherwise start a search for a party the customer is still choosing. The
 * early search already running keeps going, so opening the picker and leaving
 * the party as it was costs nothing. (The page holds off while its date
 * picker is open too.)
 *
 * Closing either picker is the customer settling that part of the search, so
 * the search starts at once rather than after the usual PREFETCH_DEBOUNCE_MS
 * (and if nothing changed, the running one is simply kept). Typing and other
 * edits still wait the debounce.
 *
 * Call this before the page's early-search effect: this hook's effect notes
 * the date picker closing, and effects run in the order they're declared, so
 * the page's effect sees the close in the same commit.
 */
export function useSearchPickers({ isCalendarOpen = false } = {}) {
  const [isTravelersOpen, setIsTravelersOpen] = useState(false);
  const closedAtRef = useRef(null);
  const calendarWasOpenRef = useRef(isCalendarOpen);

  const onTravelersOpenChange = useCallback((open) => {
    setIsTravelersOpen(open);
    if (!open) closedAtRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (calendarWasOpenRef.current && !isCalendarOpen) closedAtRef.current = Date.now();
    calendarWasOpenRef.current = isCalendarOpen;
  }, [isCalendarOpen]);

  /** 0 right after a picker closed; PREFETCH_DEBOUNCE_MS otherwise. */
  const earlySearchDelay = useCallback(() => {
    const closedAt = closedAtRef.current;
    return closedAt != null && Date.now() - closedAt < PREFETCH_DEBOUNCE_MS ? 0 : PREFETCH_DEBOUNCE_MS;
  }, []);

  return { isTravelersOpen, onTravelersOpenChange, earlySearchDelay };
}
